<template>
  <!-- Main navigation on small and medium screens (below 992px). Sidenav.vue
       renders it; above that breakpoint it stays hidden and the sidebar shows. -->
  <nav class="bottomnav" aria-label="Main navigation">
    <div class="bottomnav-inner">
      <router-link
        v-for="item in barItems"
        :key="item.key"
        :to="item.to"
        :class="['bottomnav-item', { active: item.active }]"
        :aria-current="item.active ? 'page' : null"
      >
        <span class="bottomnav-icon"><font-awesome-icon :icon="item.icon" /></span>
        <span v-if="item.badge" class="bottomnav-badge">{{ item.badge > 99 ? '99+' : item.badge }}</span>
        <span class="bottomnav-label">{{ item.shortLabel }}</span>
      </router-link>

      <button
        v-if="moreItems.length"
        ref="moreButton"
        type="button"
        :class="['bottomnav-item', { active: sheetOpen || moreActive }]"
        aria-haspopup="dialog"
        aria-controls="nav-sheet"
        :aria-expanded="sheetOpen ? 'true' : 'false'"
        @click="openSheet"
      >
        <span class="bottomnav-icon"><font-awesome-icon icon="fa-solid fa-ellipsis" /></span>
        <span v-if="moreBadge" class="bottomnav-badge">{{ moreBadge > 99 ? '99+' : moreBadge }}</span>
        <span class="bottomnav-label">More</span>
      </button>
    </div>
  </nav>

  <transition name="nav-sheet">
    <div v-if="sheetOpen" class="nav-sheet-backdrop" @click.self="closeSheet" @keydown.esc="closeSheet">
      <div id="nav-sheet" ref="sheet" class="nav-sheet" role="dialog" aria-modal="true" aria-labelledby="nav-sheet-title" tabindex="-1">
        <span class="nav-sheet-handle" aria-hidden="true" @click="closeSheet"></span>
        <div class="nav-sheet-head">
          <h2 id="nav-sheet-title" class="nav-sheet-title">More</h2>
          <button type="button" class="nav-sheet-close" aria-label="Close" @click="closeSheet">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
        <ul class="nav-sheet-list">
          <li v-for="item in moreItems" :key="item.key">
            <router-link
              :to="item.to"
              :class="['nav-sheet-link', { active: item.active }]"
              :aria-current="item.active ? 'page' : null"
              @click="closeSheet()"
            >
              <span class="nav-sheet-icon"><font-awesome-icon :icon="item.icon" /></span>
              <span class="nav-sheet-label">{{ item.label }}</span>
              <span v-if="item.badge" class="nav-sheet-badge">{{ item.badge > 99 ? '99+' : item.badge }}</span>
              <font-awesome-icon icon="fa-solid fa-chevron-right" class="nav-sheet-chevron" />
            </router-link>
          </li>
        </ul>
      </div>
    </div>
  </transition>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faEllipsis, faXmark, faChevronRight } from '@fortawesome/free-solid-svg-icons';

library.add(faEllipsis, faXmark, faChevronRight);

// Tabs that fit in the bar. With more destinations than this, the items
// flagged `bottomBar` keep their tabs, the last tab becomes "More" and the
// rest move into the sheet.
const BAR_SLOTS = 5;
const WIDE_QUERY = '(min-width: 992px)';

export default {
  name: 'BottomNav',
  props: {
    // NavItem[] from buildNavGroups (utils/navigation.ts), in display order.
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      sheetOpen: false,
      wideQuery: null,
    };
  },
  computed: {
    overflows() {
      return this.items.length > BAR_SLOTS;
    },
    barItems() {
      return this.overflows ? this.items.filter((item) => item.bottomBar).slice(0, BAR_SLOTS - 1) : this.items;
    },
    moreItems() {
      return this.overflows ? this.items.filter((item) => !this.barItems.includes(item)) : [];
    },
    moreActive() {
      return this.moreItems.some((item) => item.active);
    },
    moreBadge() {
      return this.moreItems.reduce((sum, item) => sum + (item.badge || 0), 0);
    },
  },
  watch: {
    '$route.fullPath'() {
      this.sheetOpen = false;
    },
  },
  methods: {
    openSheet() {
      this.sheetOpen = true;
      this.$nextTick(() => this.$refs.sheet?.focus());
    },
    // Focus goes back to "More" only when the sheet was closed from the
    // keyboard (Esc, or Enter/Space on the close button, which fire a click
    // with detail 0). After a tap it would leave a focus ring on the tab.
    closeSheet(event) {
      if (!this.sheetOpen) return;
      this.sheetOpen = false;
      const fromKeyboard = event?.type === 'keydown' || event?.detail === 0;
      if (fromKeyboard) this.$nextTick(() => this.$refs.moreButton?.focus());
    },
    // Rotating a tablet past the breakpoint swaps the bar for the sidebar;
    // don't leave the sheet open over the desktop layout.
    onWideChange(event) {
      if (event.matches) this.sheetOpen = false;
    },
  },
  mounted() {
    this.wideQuery = window.matchMedia(WIDE_QUERY);
    this.wideQuery.addEventListener?.('change', this.onWideChange);
  },
  beforeUnmount() {
    this.wideQuery?.removeEventListener?.('change', this.onWideChange);
  },
};
</script>

<style scoped>
/* ----------------------------------- Bar ---------------------------------- */
.bottomnav {
  display: none;
}

@media (max-width: 991.98px) {
  /* Height comes from --bottom-nav-space (sidenav.css), the same value the
     content area gives up, so the bar never covers the end of a page. */
  .bottomnav {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    box-sizing: border-box;
    height: var(--bottom-nav-space);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: #ffffff;
    border-top: 1px solid #E4E4E7;
    box-shadow: 0 -4px 16px rgba(24, 24, 27, 0.04);
  }
}

.bottomnav-inner {
  display: flex;
  align-items: stretch;
  height: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: 0 4px;
}

.bottomnav-item {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 2px;
  border: 0;
  background: transparent;
  font: inherit;
  color: #71717a !important;
  text-decoration: none !important;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.15s ease;
}

.bottomnav-icon {
  width: 56px;
  max-width: 100%;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.bottomnav-icon svg {
  font-size: 1.05rem;
  width: 1em;
  height: 1em;
}

.bottomnav-label {
  max-width: 100%;
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Five tabs on a 320px screen leave ~60px each; keep "Attendance" whole. */
@media (max-width: 359.98px) {
  .bottomnav-label {
    font-size: 0.62rem;
  }
}

@media (hover: hover) {
  .bottomnav-item:hover {
    color: #18181b !important;
  }
}

.bottomnav-item.active {
  color: #2E7918 !important;
}

.bottomnav-item.active .bottomnav-icon {
  background: #E6F2E1;
}

.bottomnav-item.active .bottomnav-label {
  font-weight: 600;
}

.bottomnav-item:focus-visible .bottomnav-icon {
  box-shadow: 0 0 0 2px #2E7918;
}

/* ---------------------------------- Sheet --------------------------------- */
.nav-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1030;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(24, 24, 27, 0.42);
}

.nav-sheet {
  width: 100%;
  max-width: 560px;
  max-height: calc(var(--app-height, 100vh) - 48px);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -12px 32px rgba(0, 0, 0, 0.14);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  overflow: hidden;
  outline: none;
}

.nav-sheet-handle {
  width: 40px;
  height: 4px;
  flex-shrink: 0;
  margin: 10px auto 0;
  border-radius: 999px;
  background: #D4D4D8;
  cursor: pointer;
}

.nav-sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px 12px 20px;
  border-bottom: 1px solid #F0F0F2;
}

.nav-sheet-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #18181b;
}

.nav-sheet-close {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #F4F4F5;
  color: #52525b;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-sheet-close:hover {
  background: #E4E4E7;
  color: #18181b;
}

.nav-sheet-list {
  flex: 1 1 auto;
  min-height: 0;
  list-style: none;
  margin: 0;
  padding: 8px 12px 14px 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.nav-sheet-link {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 8px;
  border-radius: 12px;
  color: #27272a !important;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none !important;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease, color 0.15s ease;
}

@media (hover: hover) {
  .nav-sheet-link:hover {
    background: #F4F4F5;
  }
}

.nav-sheet-link.active {
  background: #f0f9f0;
  color: #2E7918 !important;
  font-weight: 600;
}

.nav-sheet-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #F4F4F5;
  color: #52525b;
  font-size: 0.95rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-sheet-link.active .nav-sheet-icon {
  background: #2E7918;
  color: #ffffff;
}

.nav-sheet-label {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-sheet-chevron {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: #A1A1AA;
}

/* Fade the backdrop and slide the sheet up; both share one duration so the
   leave transition isn't cut short. */
.nav-sheet-enter-active,
.nav-sheet-leave-active {
  transition: opacity 0.28s ease;
}

.nav-sheet-enter-from,
.nav-sheet-leave-to {
  opacity: 0;
}

.nav-sheet-enter-active .nav-sheet,
.nav-sheet-leave-active .nav-sheet {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}

.nav-sheet-enter-from .nav-sheet,
.nav-sheet-leave-to .nav-sheet {
  transform: translateY(100%);
}

@media (prefers-reduced-motion: reduce) {
  .nav-sheet-enter-active,
  .nav-sheet-leave-active,
  .nav-sheet-enter-active .nav-sheet,
  .nav-sheet-leave-active .nav-sheet {
    transition: none;
  }
}

@media print {
  .bottomnav,
  .nav-sheet-backdrop {
    display: none !important;
  }
}
</style>
